import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Lock, ArrowRight } from "lucide-react";
import { useHideEmergentBadge } from "@/hooks/useHideEmergentBadge";

/**
 * Interstitial page shown before redirecting the merchant to Stripe Checkout.
 * - No automatic redirect — requires a manual button tap.
 * - The Stripe URL is read from sessionStorage (set by the merchant dashboard
 *   right before navigating here). It never appears in our domain's URL.
 */
export default function BillingConfirm() {
  const navigate = useNavigate();
  const [stripeUrl, setStripeUrl] = useState(null);

  useHideEmergentBadge();

  useEffect(() => {
    let url = null;
    try {
      url = window.sessionStorage.getItem("sparkloop-checkout-url");
    } catch { /* ignore */ }
    if (!url || !url.startsWith("https://checkout.stripe.com/")) {
      navigate("/merchant", { replace: true });
      return;
    }
    setStripeUrl(url);
  }, [navigate]);

  const proceed = () => {
    if (!stripeUrl) return;
    try { window.sessionStorage.removeItem("sparkloop-checkout-url"); } catch {/* ignore */}
    window.location.href = stripeUrl;
  };

  if (!stripeUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9fb]">
        <div className="text-sm uppercase tracking-[0.2em] text-[#002fa7] font-semibold">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9f9fb] p-6" data-testid="billing-confirm-page">
      <div className="bg-white border border-[#e5e5eb] rounded-3xl p-8 sm:p-10 max-w-md w-full text-center shadow-sm">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-3">
          <Sparkles className="w-3 h-3" /> SparkLoop
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight mb-4">
          Secure checkout
        </h1>
        <p className="text-[#5a5a65] mb-6 leading-relaxed">
          You are being securely redirected to <span className="font-semibold text-[#0a0a0a]">Stripe Checkout</span> to complete your subscription.
        </p>
        <div className="bg-[#f9f9fb] border border-[#e5e5eb] rounded-2xl p-4 mb-6 text-left flex items-start gap-3">
          <Lock className="w-4 h-4 text-[#009e60] mt-1 flex-shrink-0" />
          <div className="text-xs text-[#5a5a65] leading-relaxed">
            Your payment information is processed by Stripe over an encrypted connection. SparkLoop never sees or stores your card details.
          </div>
        </div>
        <button
          data-testid="billing-continue-stripe-btn"
          onClick={proceed}
          className="w-full h-14 rounded-2xl bg-[#002fa7] hover:bg-[#002585] text-white font-bold text-base inline-flex items-center justify-center gap-2 transition active:scale-[0.99] shadow-lg shadow-[#002fa7]/20"
        >
          Continue to Stripe <ArrowRight className="w-4 h-4" />
        </button>
        <button
          data-testid="billing-cancel-btn"
          onClick={() => navigate("/merchant")}
          className="mt-3 text-xs text-[#5a5a65] hover:text-[#0a0a0a] underline"
        >
          Cancel and go back
        </button>
      </div>
    </div>
  );
}
